import { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAPI } from '../Hooks/useAPIs';

export const useTimelineTable = (filters, selectedItem, debouncedFilters, dateType) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [offsetMinutes, setOffsetMinutesState] = useState(5);
    const [tableFilters, setTableFilters] = useState({
        category: [],
        type: []
    });

    const { apiCall } = useAPI();

    const timelineTableQuery = useQuery({
        queryKey: [
            'timeline-table',
            filters?.vin,
            filters?.mac_id,
            selectedItem?.datetime,
            currentPage,
            offsetMinutes,
            tableFilters,
            dateType
        ],
        queryFn: async () => {
            const params = new URLSearchParams();


            params.append('datetime', selectedItem.datetime);
            params.append('page', currentPage.toString());
            if (filters?.vin.length > 0) params.append('vin', filters.vin);
            if (filters?.mac_id.length > 0) params.append('mac_id', filters.mac_id);
            if (tableFilters.category && (Array.isArray(tableFilters.category) ? tableFilters.category.length > 0 : tableFilters.category)) {
                const categoryValue = Array.isArray(tableFilters.category) 
                    ? tableFilters.category.join(',') 
                    : tableFilters.category;
                if (categoryValue) params.append('category', categoryValue);
            }
            if (tableFilters.type && (Array.isArray(tableFilters.type) ? tableFilters.type.length > 0 : tableFilters.type)) {
                const typeValue = Array.isArray(tableFilters.type) 
                    ? tableFilters.type.join(',') 
                    : tableFilters.type;
                if (typeValue) params.append('file_type', typeValue);
            }
            if (dateType) {
            params.append('use_datetime_type', dateType);
            }
            if (offsetMinutes !== null && offsetMinutes !== undefined) {
                params.append('offset_minutes', offsetMinutes.toString());
            }
            params.append('page_size', '100');
            const response = await apiCall('GET', `loggers/timeline?${params.toString()}`);
            return response;
        },
        enabled: Boolean((filters?.vin?.length > 0 || filters?.mac_id?.length > 0) && selectedItem?.datetime),
        keepPreviousData: true,
    });

    const handlePageChange = useCallback((direction) => {
        setCurrentPage(prev => {
            let newPage = prev + direction;
            if (newPage === 0) {
            newPage = direction > 0 ? 1 : -1;
            }
            return newPage;
        });
        }, []);


    const goToNextPage = useCallback(() => handlePageChange(1), [handlePageChange]);
    const goToPreviousPage = useCallback(() => handlePageChange(-1), [handlePageChange]);

    const resetPage = useCallback(() => setCurrentPage(1), []);

    const setOffsetMinutes = useCallback((value) => {
        setOffsetMinutesState(value === null ? null : Number(value));
        setCurrentPage(1);
    }, []);

    const handleTableFilterChange = useCallback((filterType, value) => {
        setTableFilters((prev) => ({
            ...prev,
            [filterType]: value,
        }));
        setCurrentPage(1);
    }, []);

    const clearTableFilters = useCallback(() => {
        setTableFilters({ category: [], type: [] });
        setCurrentPage(1);
    }, []);

    useEffect(() => {
        resetPage();
        clearTableFilters();
    }, [selectedItem?.vin, selectedItem?.datetime]);

    return {
        timelineTableQuery,
        currentPage,
        offsetMinutes,
        setOffsetMinutes,
        tableFilters,
        goToNextPage,
        goToPreviousPage,
        resetPage,
        handleTableFilterChange,
        clearTableFilters,
        hasNextPage: timelineTableQuery.data?.hasNext || false,
        hasPreviousPage: currentPage > 1,
    };
};
